import React, {useState, useMemo, useCallback} from 'react';
import { useSelector } from "react-redux";
import {PieChart as RechartsPieChart, Pie, Cell, Tooltip, Sector} from 'recharts';
import {Box, Typography, Grid, Stack, useMediaQuery} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {OVERALL_DOCUMENT_STATUS_COLORS} from '../../../../constants/peppolStatusConstants';
import {formatAccessDeniedMessage} from "../../../../utils/accessDeniedMessage";
import AccessDeniedCard from "../../../../components/AccessDeniedCard";
import AccessDeniedComplienceSection from "../AccessDeniedComplienceSection";

const DEFAULT_STATUS_LABELS = {
    approved: 'Approved',
    rejected: 'Rejected',
    pending: 'Pending',
    validationFailed: 'Validation Failed',
};

const MIN_PERCENTAGE = 1; // Minimum percentage for visibility of small segments

const STATUS_ORDER = ['pending', 'approved', 'rejected', 'validationFailed'];

const DocumentStatusPieChart = ({data, loading, title = 'Document Status Overview', accessDenied = false}) => {
    const theme = useTheme();
    const STATUS_LABELS = useMemo(
        () => ({
            approved: DEFAULT_STATUS_LABELS.approved,
            rejected: DEFAULT_STATUS_LABELS.rejected,
            pending: DEFAULT_STATUS_LABELS.pending,
            validationFailed: DEFAULT_STATUS_LABELS.validationFailed,
        }),
        [DEFAULT_STATUS_LABELS],
    );
    const [activeIndex, setActiveIndex] = useState(null);
    const [mousePosition, setMousePosition] = useState({x: 0, y: 0});
    const [hasAnimated, setHasAnimated] = useState(false);

    const rootUserEmail = useSelector((state) => state.org.org?.root_user_email ?? '');

    // Use MUI breakpoints for responsive sizing
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));
    const isSm = useMediaQuery(theme.breakpoints.only('sm'));
    const isMd = useMediaQuery(theme.breakpoints.only('md'));

    // Calculate chart size based on breakpoints
    const chartSize = useMemo(() => {
        if (isXs) return 250;
        if (isSm) return 300;
        if (isMd) return 350;
        return 380;
    }, [isXs, isSm, isMd]);

    // Memoize color mapping to avoid recalculation
    const COLORS = useMemo(() => {
        const getColorFromStatus = (status) => {
            const statusColor = OVERALL_DOCUMENT_STATUS_COLORS[status];
            const colorMap = {
                success: theme.palette.success.main,
                info: theme.palette.info.main,
                error: theme.palette.error.main,
                warning: theme.palette.warning.main,
            };
            return colorMap[statusColor] || theme.palette.grey[500];
        };

        return {
            approved: getColorFromStatus('APPROVED'),
            rejected: getColorFromStatus('REJECTED'),
            pending: getColorFromStatus('PENDING'),
            validationFailed: getColorFromStatus('VALIDATION_FAILED'),
        };
    }, [theme]);

    // Calculate chart data with minimum height for non-zero values
    const chartDataWithMinHeight = useMemo(() => {
        const total = data?.total || 0;

        // Build raw data array
        const rawData = STATUS_ORDER.map((name) => ({
            name,
            value: data[name] || 0,
            fill: COLORS[name],
        }));

        // Early return for zero total
        if (total === 0) {
            return rawData.map((item) => ({
                ...item,
                percentage: 0,
                adjustedValue: item.value,
            }));
        }

        // Single pass: calculate percentages and identify segments needing adjustment
        let totalPercentageToRedistribute = 0;
        let totalAdjustablePercentage = 0;

        const dataWithPercentages = rawData.map((item) => {
            const percentage = (item.value / total) * 100;
            const needsMin = item.value > 0 && percentage < MIN_PERCENTAGE;
            const isAdjustable = item.value > 0 && percentage >= MIN_PERCENTAGE;

            if (needsMin) {
                totalPercentageToRedistribute += MIN_PERCENTAGE - percentage;
            } else if (isAdjustable) {
                totalAdjustablePercentage += percentage;
            }

            return {
                ...item,
                percentage,
                needsMin,
                isAdjustable,
            };
        });

        // Calculate reduction factor if redistribution is needed
        const reductionFactor =
            totalAdjustablePercentage > 0 ? 1 - totalPercentageToRedistribute / totalAdjustablePercentage : 1;

        // Apply adjustments in single pass
        return dataWithPercentages.map((item) => {
            if (item.needsMin) {
                return {
                    ...item,
                    adjustedValue: (MIN_PERCENTAGE / 100) * total,
                };
            }
            if (item.isAdjustable) {
                return {
                    ...item,
                    adjustedValue: item.value * reductionFactor,
                };
            }
            return {
                ...item,
                adjustedValue: item.value,
            };
        });
    }, [data, COLORS]);

    // Transform to chart format and calculate total once
    const {chartData, total} = useMemo(() => {
        const totalValue = data?.total || 0;
        const chartDataTransformed = chartDataWithMinHeight.map((item) => ({
            name: item.name,
            value: item.adjustedValue,
            fill: item.fill,
            originalValue: item.value, // Keep original for display
        }));
        return {chartData: chartDataTransformed, total: totalValue};
    }, [chartDataWithMinHeight, data?.total]);

    // Background data for gray base donut - always show full circle
    const backgroundData = [{name: 'background', value: 100, fill: theme.palette.grey[300]}];

    // Calculate chart dimensions once
    const {center, innerRadius, outerRadius} = useMemo(
        () => ({
            center: chartSize / 2,
            innerRadius: chartSize * 0.18,
            outerRadius: chartSize * 0.3,
        }),
        [chartSize],
    );

    // Custom tooltip that follows mouse pointer
    const CustomTooltip = useCallback(
        ({active, payload}) => {
            if (!active || !payload?.length) return null;

            // Find the first payload item that's not the background segment
            const dataItem =
                payload.find((item) => {
                    const name = item.name || item.payload?.name;
                    return name && name !== 'background' && STATUS_LABELS[name];
                }) || payload[0];

            const segmentName = dataItem.name || dataItem.payload?.name;
            const segmentValue =
                dataItem.payload?.originalValue ?? dataItem.originalValue ?? dataItem.value ?? dataItem.payload?.value ?? 0;

            // Skip if it's the background or not a valid status
            if (!segmentName || segmentName === 'background' || !STATUS_LABELS[segmentName]) return null;

            const tooltipOffset = 8;
            const tooltipLeft = mousePosition.x + (tooltipOffset);


            return (
                <Box
                    component="div"
                    style={{
                        position: 'fixed',
                        left: `${tooltipLeft}px`,
                        top: `${mousePosition.y - tooltipOffset}px`,
                        transform: 'translate(0, -100%)',
                        transition: 'opacity 0.2s ease-in-out, left 0.05s linear, top 0.05s linear',
                    }}
                    sx={{
                        bgcolor: 'white',
                        p: 1.5,
                        borderRadius: 1,
                        boxShadow: 3,
                        zIndex: 9999,
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        opacity: active ? 1 : 0,
                    }}
                >
                    <Typography variant="body2" sx={{fontWeight: 600}}>
                        {STATUS_LABELS[segmentName]}: {Math.round(segmentValue)}
                    </Typography>
                </Box>
            );
        },
        [mousePosition, STATUS_LABELS],
    );

    // Custom active shape that smoothly pops out on hover
    const renderActiveShape = useCallback((props) => {
        const {cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill} = props;

        return (
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius + 12}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                opacity={0.95}
            />
        );
    }, []);

    const getAccessDeniedMsg = () => {

        const ACCESS_DENIED_MSG = "Access denied, please contact your organization admin ({email}) to get access."

        return (
            <Box
                sx={{flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 0,}}
            >
                <Typography variant="body2" color="textDisabled" padding="1rem" align="left">{formatAccessDeniedMessage(ACCESS_DENIED_MSG, rootUserEmail)}</Typography>
            </Box>
        );
    }

    const getDonutChart = () => {
        return (
            <>
                <Box
                    onMouseMove={handleMouseMove}
                    sx={{
                        width: '100%',
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 0,
                        py: 0,
                        position: 'relative',
                        '& svg, & svg *, &, & *': {
                            outline: 'none !important',
                            border: 'none !important',
                            '&:focus, &:focus-visible': {
                                outline: 'none !important',
                                border: 'none !important',
                            },
                        },
                        '& svg': {
                            maxWidth: {xs: '250px', sm: '300px', md: '350px', lg: '380px'},
                            width: '100%',
                            height: 'auto',
                            '& path': {
                                transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                transformOrigin: 'center',
                                '&:hover': {
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                },
                            },
                        },
                    }}
                >
                    <RechartsPieChart width={chartSize} height={chartSize}>
                        {/* Gray background donut */}
                        <Pie
                            data={backgroundData}
                            dataKey="value"
                            cx={center}
                            cy={center}
                            innerRadius={innerRadius}
                            outerRadius={outerRadius}
                            isAnimationActive={false}
                            fill={theme.palette.grey[300]}
                        >
                            {backgroundData.map((entry, index) => (
                                <Cell key={`bg-cell-${index}`} fill={entry.fill}/>
                            ))}
                        </Pie>
                        {/* Data donut on top */}
                        {chartData.length > 0 && (
                            <Pie
                                data={chartData}
                                dataKey="value"
                                cx={center}
                                cy={center}
                                innerRadius={innerRadius}
                                outerRadius={outerRadius}
                                isAnimationActive={!hasAnimated}
                                animationDuration={800}
                                activeShape={activeIndex !== null ? renderActiveShape : undefined}
                                activeIndex={activeIndex}
                                onMouseEnter={onPieEnter}
                                onMouseLeave={onPieLeave}
                                onAnimationEnd={() => setHasAnimated(true)}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.fill}
                                        style={{
                                            cursor: 'pointer',
                                            opacity: activeIndex === index ? 1 : activeIndex !== null ? 0.5 : 1,
                                            transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        }}
                                    />
                                ))}
                            </Pie>
                        )}
                        <Tooltip
                            content={CustomTooltip}
                            isAnimationActive={false}
                            allowEscapeViewBox={{x: true, y: true}}
                            cursor={false}
                        />
                    </RechartsPieChart>
                    {/* Center Text - Total Documents */}
                    <Stack
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            pointerEvents: 'none',
                        }}
                    >
                        {loading ? (
                            ''
                        ) : (
                            <>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {total}
                                </Typography>
                                <Typography variant="body2" color={theme.palette.grey[600]} fontWeight="500">
                                    {"Documents"}
                                </Typography>
                            </>
                        )}
                    </Stack>
                </Box>
                {/* Legend */}
                <Box sx={{width: '100%', flexShrink: 0, mb: 1}}>
                    <Stack direction="column" alignItems="center" spacing={1}>
                        <Grid container spacing={1.5} justifyContent="center" sx={{mb: 0.25}}>
                            {chartData.map((entry) => (
                                <Grid size="auto" key={entry.name}>
                                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75}>
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                bgcolor: entry.fill,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography variant="body2" sx={{fontWeight: 500}}>
                                            <Box component="span" sx={{color: entry.fill}}>
                                                {STATUS_LABELS[entry.name]}:
                                            </Box>
                                        </Typography>
                                        <Typography variant="body2" sx={{fontWeight: 500}}>
                                            <Box component="span" sx={{color: theme.palette.text.primary}}>
                                                {loading ? '0' : (entry.originalValue ?? entry.value)}
                                            </Box>
                                        </Typography>
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                    </Stack>
                </Box>
            </>
        );
    }
    const handleMouseMove = useCallback((e) => {
        const clientX = e?.clientX ?? e?.nativeEvent?.clientX;
        const clientY = e?.clientY ?? e?.nativeEvent?.clientY;

        if (clientX !== undefined && clientY !== undefined) {
            setMousePosition({
                x: clientX,
                y: clientY,
            });
        }
    }, []);

    const onPieEnter = useCallback((_, index) => {
        setActiveIndex(index);
    }, []);

    const onPieLeave = useCallback(() => {
        setActiveIndex(null);
    }, []);

    return (
        <Box
            sx={{
                p: 0.5,
                bgcolor: theme.palette.background.paper,
                borderRadius: 2,
                boxShadow: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                outline: 'none !important',
                border: 'none',
                '&:focus, &:focus-visible': {
                    outline: 'none !important',
                },
            }}
        >
            <Typography variant="h6" sx={{m: 1, fontWeight: 600}}>
                {title}
            </Typography>

            <Stack
                direction="column"
                alignItems="center"
                spacing={0}
                sx={{flex: 1, justifyContent: 'flex-start', width: '100%', minHeight: 0}}
            >
                {/* Donut Chart */}
                {accessDenied ? <AccessDeniedComplienceSection/> : getDonutChart()}
            </Stack>
        </Box>
    );
};

export default DocumentStatusPieChart;
